/**
 * PrometheusMark — Prometheus Lab's logo: the same galaxy as AuraOS (two arms growing out
 * of a glowing core, thinning to a point) in fire tones, so the two apps read as siblings.
 * `animated` turns it slowly while the core breathes; `thinking` spins it fast. Both stop
 * when the user prefers reduced motion. `simple` drops the sparkles for ≤ 24 px.
 * Geometry is shared with jarvis-app's AuraMark.tsx and scripts/gen_brand_icons.py.
 */
import { useId, useSyncExternalStore } from "react";
import { cn } from "@/lib/utils";

interface PrometheusMarkProps {
  className?: string;
  animated?: boolean;
  /** Fast spin for loading / Aura working; implies `animated`. */
  thinking?: boolean;
  simple?: boolean;
  /** Accessible name; omit when a visible "Prometheus" label sits next to it. */
  title?: string;
}

// Geometry on the 512 grid (same numbers as gen_brand_icons.py)
const TURNS = 1.2;
const R1 = 218;
const W0 = 118;
const SPARKLES: [number, number, number][] = [
  [380, 120, 16],
  [118, 388, 13],
  [420, 330, 9],
  [96, 150, 8],
  [300, 450, 6],
];

/** One arm: swells out of the core and thins to a sharp tip. */
function armPath(start: number, steps = 160): string {
  const left: string[] = [];
  const right: string[] = [];
  const dr = R1 / (2 * Math.PI * TURNS);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const w = W0 * (1 - t) ** 1.25 * (0.55 + 0.45 * Math.cos((t * Math.PI) / 2));
    const a = ((start + 360 * TURNS * t) * Math.PI) / 180;
    const r = R1 * t ** 0.92;
    const tx = dr * Math.cos(a) - r * Math.sin(a);
    const ty = dr * Math.sin(a) + r * Math.cos(a);
    const n = Math.hypot(tx, ty) || 1;
    const nx = -ty / n;
    const ny = tx / n;
    const x = 256 + r * Math.cos(a);
    const y = 256 + r * Math.sin(a);
    left.push(`${(x + (nx * w) / 2).toFixed(1)} ${(y + (ny * w) / 2).toFixed(1)}`);
    right.push(`${(x - (nx * w) / 2).toFixed(1)} ${(y - (ny * w) / 2).toFixed(1)}`);
  }
  return `M${[...left, ...right.reverse().slice(1)].join(" L")} Z`;
}

/** Four-point star with curved sides, centered on 0,0. */
function sparklePath(s: number): string {
  const k = s * 0.22;
  return `M0 ${-s} Q${k} ${-k} ${s} 0 Q${k} ${k} 0 ${s} Q${-k} ${k} ${-s} 0 Q${-k} ${-k} 0 ${-s} Z`;
}

const ARMS = [armPath(90), armPath(270)];

const REDUCE_QUERY = "(prefers-reduced-motion: reduce)";
function subscribeReduce(cb: () => void) {
  const mq = window.matchMedia(REDUCE_QUERY);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}
function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReduce, () => window.matchMedia(REDUCE_QUERY).matches, () => false);
}

export function PrometheusMark({ className, animated = false, thinking = false, simple = false, title }: PrometheusMarkProps) {
  const id = useId().replace(/:/g, "");
  const reduce = useReducedMotion();
  const live = (animated || thinking) && !reduce;
  const spin = thinking ? "3.2s" : "48s";
  const pulse = thinking ? "1.4s" : "5s";

  return (
    <svg
      viewBox="0 0 512 512"
      className={cn("shrink-0", className)}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <defs>
        <radialGradient id={`${id}-gal`} cx="256" cy="256" r="232" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.14" stopColor="#fff7d6" />
          <stop offset="0.34" stopColor="#fbbf24" />
          <stop offset="0.62" stopColor="#f97316" />
          <stop offset="0.86" stopColor="#ef4444" />
          <stop offset="1" stopColor="#be123c" />
        </radialGradient>
        <radialGradient id={`${id}-halo`}>
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="0.3" stopColor="#fef3c7" stopOpacity="0.45" />
          <stop offset="1" stopColor="#fbbf24" stopOpacity="0" />
        </radialGradient>
      </defs>
      <g>
        {ARMS.map((d) => (
          <path key={d.slice(0, 12)} d={d} fill={`url(#${id}-gal)`} />
        ))}
        {live && (
          <animateTransform attributeName="transform" type="rotate" from="0 256 256" to="360 256 256" dur={spin} repeatCount="indefinite" />
        )}
      </g>
      <circle cx="256" cy="256" r="50" fill={`url(#${id}-gal)`} />
      <circle cx="256" cy="256" r="118" fill={`url(#${id}-halo)`}>
        {live && (
          <>
            <animate attributeName="r" values="106;132;106" dur={pulse} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.75;1;0.75" dur={pulse} repeatCount="indefinite" />
          </>
        )}
      </circle>
      {!simple &&
        SPARKLES.map(([x, y, s], i) => (
          <g key={i} transform={`translate(${x} ${y})`} fill="#fff7ed">
            <path d={sparklePath(s)}>
              {live && (
                <>
                  <animateTransform attributeName="transform" type="scale" values="0.4;1;0.4" dur="3.6s" begin={`${i * 0.7}s`} repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.25;1;0.25" dur="3.6s" begin={`${i * 0.7}s`} repeatCount="indefinite" />
                </>
              )}
            </path>
          </g>
        ))}
    </svg>
  );
}
